import type { Translate } from 'next-translate';
import React from 'react';
import { Container } from '@/components/elements/Container';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { MyImage } from '@/components/elements/MyImage';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import type { LandingTools } from '@/types';
import bgMobile from '@/public/images/landing/backgrounds/bg-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg.webp';
import styles from './ToolsGrid.module.scss';

interface ToolsGridProps {
	data: LandingTools['hero'];
	t: Translate;
	withBackground?: boolean;
}
export const ToolsGrid: React.FC<ToolsGridProps> = ({
	data,
	t,
	withBackground = true,
}) => {
	const sortedData = data.list.sort((a, b) => b.id - a.id);

	// Use English translations for cards (cards are not translated)
	const { t: tEn } = useTranslationWithLocale('tools', 'en');

	const getCardText = (key: string): string => {
		// For cards, always use English
		if (key.startsWith('heroData_cards_')) {
			// Convert heroData_cards_card1 to heroData.cards.card1
			const cardKey = key.replace('heroData_cards_', 'heroData.cards.');
			return tEn(cardKey) || tEn(key) || key;
		}
		return t(key);
	};

	return (
		<section className={styles['tools']}>
			{withBackground && (
				<MyImage
					imageProps={{
						src: bgMobile,
						alt: 'background image',
						title: `${t(data.title)}`,
						priority: true,
						fetchPriority: 'high',
						'aria-hidden': true,
					}}
					settings={{
						classes: {
							picture: `${styles['tools__background']}`,
							image: styles['tools__background-img'],
						},
					}}
				>
					<source srcSet={bg.src} media='(min-width: 1023px)' />
				</MyImage>
			)}

			<Container className={styles['tools__container']}>
				<h1 className={styles['tools__title']}>{t(data.title)}</h1>

				<div className={styles['tools__grid']}>
					{sortedData.map(({ id, image, alt, title, href }) => (
						<LinkWithQuery
							href={href ?? ''}
							key={id}
							className={styles['tools__card']}
							aria-label={getCardText(title)}
						>
							<MyImage
								imageProps={{
									src: image,
									alt: getCardText(alt),
									title: getCardText(title),
									loading: 'lazy',
								}}
								settings={{
									classes: {
										picture: `${styles['tools__picture']}`,
										image: styles['tools__image'],
									},
								}}
							/>
							<h2 className={styles['tools__card-title']}>
								{getCardText(title)}
							</h2>
						</LinkWithQuery>
					))}
				</div>
			</Container>
		</section>
	);
};
